import { useAuth } from "@um/ui";
import type { Mind, WithRef } from "@um/contracts";

import { useProfilesStore } from "@/store/useProfilesStore";

/** The mind the reward loop reads and writes. */
export function useRewardMind(): WithRef<Mind> | undefined {
  const { accountId, userMind, role, selectedMindRef } = useAuth();
  const profiles = useProfilesStore((s) => s.profiles);
  const subscribedAccountId = useProfilesStore((s) => s.subscribedAccountId);

  if (role !== "member" && selectedMindRef) {
    // Auth already streams this profile; its claims remain available even if
    // a page or dialog has stopped the shared profiles collection listener.
    if (
      userMind && !userMind.isDeleted &&
      userMind.ref.path === selectedMindRef.path &&
      userMind.ref.path === `Account/${accountId}/Mind/${userMind.ref.id}`
    ) return userMind;
    return subscribedAccountId === accountId
      ? profiles.find((profile) => profile.ref.path === selectedMindRef.path)
      : undefined;
  }
  if (userMind) return userMind;

  // Keyed on `subscribedAccountId`, not `profiles.length`. An unsubscribed
  // store reads as an empty array, which is indistinguishable from an account
  // that genuinely has no minds — and treating "not loaded yet" as "no mind"
  // is precisely what would drop a claim.
  if (!accountId || subscribedAccountId !== accountId) return undefined;

  // Exactly one, or nothing. On an account carrying several people, which
  // ladder this is cannot be inferred, and guessing writes progress onto
  // somebody else.
  return profiles.length === 1 ? profiles[0] : undefined;
}
